import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableCell,
  TableRow,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../config/api';
import DataTable from '../components/DataTable';

function ServiceTypes() {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    reduction_amount: '',
    status: 'active',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchServiceTypes();
  }, []);

  const fetchServiceTypes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/service-types');
      setServiceTypes(response.data.data || []);
    } catch (error) {
      console.error('Error fetching service types:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '_') // Replace spaces and hyphens with underscores
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  };

  const handleNameChange = (name) => {
    setFormData({
      ...formData,
      name,
      slug: editing ? formData.slug : generateSlug(name), // Only auto-generate on create
    });
  };

  const handleOpen = (serviceType = null) => {
    if (serviceType) {
      setEditing(serviceType);
      setFormData({
        name: serviceType.name || '',
        slug: serviceType.slug || '',
        description: serviceType.description || '',
        reduction_amount: serviceType.reduction_amount || '',
        status: serviceType.status || 'active',
      });
    } else {
      setEditing(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        reduction_amount: '',
        status: 'active',
      });
    }
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      reduction_amount: '',
      status: 'active',
    });
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setError('');

      // Validation
      if (!formData.name || !formData.slug) {
        setError('Name and slug are required');
        return;
      }

      // Validate slug format
      const slugRegex = /^[a-z0-9_-]+$/;
      if (!slugRegex.test(formData.slug)) {
        setError('Slug must be lowercase alphanumeric with underscores or hyphens only');
        return;
      }

      const dataToSend = {
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        description: formData.description?.trim() || null,
        reduction_amount: formData.reduction_amount ? parseFloat(formData.reduction_amount) : 0.00,
        status: formData.status || 'active',
      };

      if (editing) {
        await api.put(`/service-types/${editing.id}`, dataToSend);
      } else {
        await api.post('/service-types', dataToSend);
      }
      handleClose();
      fetchServiceTypes();
    } catch (error) {
      setError(error.response?.data?.error || 'Error saving service type');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service type?')) {
      try {
        await api.delete(`/service-types/${id}`);
        fetchServiceTypes();
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Error deleting service type';
        alert(errorMessage);
        console.error('Error deleting service type:', error);
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Service Types</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Service Type
        </Button>
      </Box>

      <DataTable
        data={serviceTypes}
        loading={loading}
        searchFields={['name', 'slug', 'description']}
        emptyMessage="No service types found"
        columns={[
          { field: 'id', label: 'ID' },
          { field: 'name', label: 'Name' },
          { field: 'slug', label: 'Slug' },
          { field: 'description', label: 'Description' },
          { field: 'reduction_amount', label: 'Reduction Amount' },
          { field: 'status', label: 'Status' },
          { field: 'created_at', label: 'Created At' },
          { field: 'actions', label: 'Actions' },
        ]}
        renderRow={(serviceType) => (
          <TableRow key={serviceType.id}>
            <TableCell>{serviceType.id}</TableCell>
            <TableCell>{serviceType.name}</TableCell>
            <TableCell>
              <Chip label={serviceType.slug} size="small" variant="outlined" />
            </TableCell>
            <TableCell>{serviceType.description || '-'}</TableCell>
            <TableCell>₹{parseFloat(serviceType.reduction_amount || 0).toFixed(2)}</TableCell>
            <TableCell>
              <Chip
                label={serviceType.status}
                color={serviceType.status === 'active' ? 'success' : 'default'}
                size="small"
              />
            </TableCell>
            <TableCell>
              {serviceType.created_at
                ? new Date(serviceType.created_at).toLocaleDateString()
                : '-'}
            </TableCell>
            <TableCell>
              <IconButton size="small" onClick={() => handleOpen(serviceType)}>
                <EditIcon />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(serviceType.id)}>
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        )}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Service Type' : 'Add Service Type'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            margin="normal"
            required
            helperText="Service type name (e.g., Home Services, Online Services)"
          />
          <TextField
            fullWidth
            label="Slug"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().trim() })}
            margin="normal"
            required
            helperText="URL-friendly identifier (lowercase, alphanumeric, underscores, hyphens)"
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
            helperText="Optional description of the service type"
          />
          <TextField
            fullWidth
            label="Reduction Amount"
            type="number"
            value={formData.reduction_amount}
            onChange={(e) => setFormData({ ...formData, reduction_amount: e.target.value })}
            margin="normal"
            inputProps={{ min: 0, step: 0.01 }}
            helperText="Amount to reduce (in ₹). Default: 0.00"
            onWheel={(e) => e.target.blur()}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ServiceTypes;

